import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '../../../contexts/AuthContext';
import { useInvitation } from '../hooks/useInvitation';
import { useAcceptInvitation } from '../hooks/useAcceptInvitation';
import { useSignUpWithInvitation } from '../hooks/useSignUpWithInvitation';
import { Input, PasswordInput, ErrorPage } from '../../../components/shared';

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { invitation, loading, error } = useInvitation(token!);
  const { acceptInvitation, loading: acceptLoading } = useAcceptInvitation();
  const { signUpWithInvitation, loading: signUpLoading } = useSignUpWithInvitation();

  const [signUpData, setSignUpData] = useState({
    password: '',
    passwordConfirmation: '',
    fullName: '',
  });


  const handleAccept = async () => {
    if (!currentUser) return;

    try {
      await acceptInvitation(token!);
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to accept invitation:', err);
    }
  };

  const handleSignUpAndAccept = async () => {
    try {
      await signUpWithInvitation({
        token: token!,
        ...signUpData,
      });

      // The mutation handles logging the user in automatically
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to sign up and accept invitation:', err);
    }
  };

  if (error && !loading) return <ErrorPage message={`Error: ${error.message}`} />;
  if (!invitation && !loading) return <ErrorPage message="Invalid invitation" />;

  // Check if invitation is expired or already used
  const isExpired = new Date(invitation.expiresAt) < new Date();
  const isUsed = invitation.status !== 'PENDING';

  if (isExpired || isUsed) {
    return (
      <div className="min-h-screen bg-primary-50 dark:bg-primary-950 py-24">
        <div className="container-elegant max-w-md">
          <Card>
            <CardContent className="p-8 text-center">
              <h1 className="text-2xl font-light tracking-tight text-primary-950 dark:text-white mb-4">
                {isExpired ? 'Invitation Expired' : 'Invitation Already Used'}
              </h1>
              <p className="text-primary-600 dark:text-primary-400 mb-6">
                {isExpired
                  ? 'This invitation has expired. Please contact the project administrator for a new invitation.'
                  : 'This invitation has already been accepted or cancelled.'
                }
              </p>
              <Button onClick={() => navigate('/login')} size="lg">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-primary-950 py-24">
      <div className="container-elegant max-w-2xl">
        <Card>
          <CardContent className="p-8">
            <h1 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white mb-6">
              Convite para Projeto
            </h1>

            <div className="mb-8">
              <p className="text-primary-700 dark:text-primary-300 mb-4">
                <span className="font-medium">{invitation.inviterName}</span> convidou você para participar do projeto:
              </p>

              <div className="bg-primary-50 dark:bg-primary-900 p-6 mb-6">
                <h2 className="text-xl font-light text-primary-950 dark:text-white mb-2">
                  {invitation.projectName}
                </h2>
                {invitation.project?.address && (
                  <p className="text-primary-600 dark:text-primary-400">{invitation.project.address}</p>
                )}
                {invitation.project?.creator && (
                  <p className="text-sm text-primary-500 dark:text-primary-500 mt-2">
                    Created by {invitation.project.creator.fullName}
                  </p>
                )}
              </div>

              <p className="text-sm text-primary-600 dark:text-primary-400">
                Este convite expira em {new Date(invitation.expiresAt).toLocaleDateString('pt-BR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}.
              </p>
            </div>

            {currentUser ? (
              // User is logged in
              <div>
                <p className="text-primary-700 dark:text-primary-300 mb-6">
                  Você está atualmente conectado como <span className="font-medium">{currentUser.email}</span>.
                </p>

                {currentUser.email === invitation.email ? (
                  <Button
                    onClick={handleAccept}
                    disabled={acceptLoading}
                    size="lg"
                    className="w-full"
                  >
                    {acceptLoading ? 'ACEITANDO...' : 'ACEITAR CONVITE'}
                  </Button>
                ) : (
                  <div className="text-center">
                    <p className="text-red-600 dark:text-red-400 mb-4">
                      Este convite foi enviado para {invitation.email}, mas você está conectado como {currentUser.email}.
                    </p>
                    <Button
                      onClick={() => navigate('/dashboard')}
                      size="lg"
                    >
                      GO TO DASHBOARD
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              // User is not logged in
              <div>
                {invitation.userExists ? (
                  // User exists, show login prompt
                  <div className="text-center">
                    <p className="text-primary-700 dark:text-primary-300 mb-6">
                      Já existe uma conta com {invitation.email}. Por favor, faça login para aceitar o convite.
                    </p>
                    <Button
                      onClick={() => navigate('/login')}
                      size="lg"
                    >
                      ENTRAR
                    </Button>
                  </div>
                ) : (
                  // User doesn't exist, show sign up form
                  <div>
                    <p className="text-primary-700 dark:text-primary-300 mb-6">
                      Para aceitar este convite, você precisa criar uma conta.
                    </p>

                    <div className="space-y-4 mb-6">
                      <Input
                        label="Email Address"
                        type="email"
                        value={invitation.email}
                        disabled
                        loading={loading}
                        variant="primary"
                      />

                      <Input
                        label="Nome Completo *"
                        type="text"
                        value={signUpData.fullName}
                        onChange={(e) => setSignUpData({ ...signUpData, fullName: e.target.value })}
                        loading={loading}
                        variant="primary"
                      />

                      <PasswordInput
                        label="Password *"
                        value={signUpData.password}
                        onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                        showPasswordHint={false}
                        loading={loading}
                        variant="primary"
                      />

                      <PasswordInput
                        label="Confirm Password *"
                        value={signUpData.passwordConfirmation}
                        onChange={(e) => setSignUpData({ ...signUpData, passwordConfirmation: e.target.value })}
                        showPasswordHint={false}
                        loading={loading}
                        variant="primary"
                      />
                    </div>

                    <Button
                      onClick={handleSignUpAndAccept}
                      disabled={signUpLoading || !signUpData.fullName || !signUpData.password || !signUpData.passwordConfirmation}
                      size="lg"
                      className="w-full"
                    >
                      {signUpLoading ? 'CRIANDO CONTA...' : 'CRIAR CONTA E ACEITAR CONVITE'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}